// ============================================================
// vehiclePath.js
//
// The "brain" of the bay-approach simulation. Given a door, a lift, and a
// vehicle class, this works out a real drive path from the door to the
// lift's target pose, using dubins.js for the actual curve math, and
// returns how many gear-direction changes ("corrections") it took — or
// flags the approach as blocked and says why.
//
// This is deliberately the ONE place this logic lives — both the 2D
// overlay (CadCanvas) and the 3D animation (Canvas3D) call this same
// function and render whatever it returns, so the two views can never
// visually disagree about the same door/lift/vehicle combination.
//
// ---- How it decides how many corrections are needed ----
// 1. Try a direct forward-only path (0 corrections). This is what a
//    4-post/scissor lift almost always resolves to, since the target
//    tolerance is wide.
// 2. If that path collides with something or leaves the bay, try a
//    "drive forward to a staging point, then reverse into the target"
//    maneuver (1 correction) across a small set of candidate staging
//    points.
// 3. If that still doesn't work, try a 2-correction maneuver (forward,
//    reverse, forward) across a smaller candidate set.
// 4. If nothing up to 2 corrections works, report blocked with a reason
//    inferred from why the candidates failed (too tight a turn, not
//    enough width, or ran out of room).
//
// ---- Known limitations (read before treating this as gospel) ----
// - This is a bounded heuristic search over a handful of hand-picked
//   staging-point candidates, not an exhaustive planner — it can report
//   MORE corrections than a truly optimal driver would need, though it
//   should never report fewer than are actually necessary (every
//   candidate it accepts is checked against real collision + radius
//   constraints). Treat the corrections count as "a realistic number,"
//   not "the provably fewest possible."
// - Collision is real geometry (see collision.js): the vehicle is its
//   true length × width rectangle turned to its heading, each item counts
//   only its solid parts (posts, runways, footprints), walls are the real
//   outline with door openings cut out, and 1 inch of clearance is all
//   that's required. Paths are sampled every 0.5 ft, so a graze shorter
//   than that between samples can slip through.
// - Only checks OTHER placed equipment + the outer bay walls as
//   obstacles. It doesn't know about anything outside the building
//   (parking lot layout, other vehicles queued at the door, etc).
// ============================================================

import { planForwardAll, planReverseAll, sampleSegments, endPose } from './dubins';
import { getDoorEndpoints, computeBbox } from './shopGeometry';
import { itemSolids, wallSegments, checkPose } from './collision';
import { getVehicle } from '../data/vehicleCatalog';

// How close (feet) and how aligned (degrees) the vehicle must be to the
// lift's target pose to count as "arrived." Tighter for lifts that
// engage a specific point on the vehicle (2-post arms, in-ground
// runways), looser for lifts that are just a wide drive-on platform.
// Placeholder values — tune once real lift geometry is confirmed.
const APPROACH_TOLERANCE = {
  '2post': { lateralFt: 1.5, headingDeg: 8 },
  inground: { lateralFt: 0.75, headingDeg: 5 },
  '4post': { lateralFt: 3.5, headingDeg: 20 },
  scissor: { lateralFt: 3.5, headingDeg: 20 },
  mobile_column: { lateralFt: 2, headingDeg: 15 },
};

function deg2rad(d) { return (d * Math.PI) / 180; }

// ---------- Pose construction ----------

// The vehicle's starting pose is the selected overhead door's midpoint.
// By default, heading INTO the shop (opposite of the door's outward-
// facing vector) — nose-first entry, driving in Forward.
//
// `reverse: true` gives the "back in" variant instead: heading points
// OUTWARD (toward the door, nose facing the street) because the vehicle
// is backing INTO the shop rear-first — its nose stays pointed out the
// door while the whole body moves in reverse. Paired with a first leg
// solved in reverse gear (see solveBetweenPoses' `startGear` param), this
// lets the solver consider "back through the door, then drive forward to
// the lift" as a real alternative strategy, not just nose-in-then-correct.
export function doorEntryPose(door, shop, reverse = false) {
  const ends = getDoorEndpoints(door, shop);
  if (!ends) return null;
  const heading = reverse
    ? Math.atan2(ends.outwardVec.y, ends.outwardVec.x)
    : Math.atan2(-ends.outwardVec.y, -ends.outwardVec.x);
  return { x: ends.midpoint.x, y: ends.midpoint.y, heading };
}

// The lift's target pose is its placement center, facing along one of its
// two axis-aligned directions (its rotation, or that +180°) — a 2-post/
// 4-post/scissor lift is symmetric front-to-back, the vehicle can arrive
// from either end. Returns BOTH candidates rather than guessing one: which
// end is actually reachable depends on what's around the lift (a wall,
// a neighboring lift, which door is in use), not just which one requires
// the smaller final turn — a "closer heading" guess can pick the one
// orientation that's physically blocked while the other works fine, so
// the caller tries both and keeps whichever actually solves.
export function liftTargetPoses(liftItem) {
  const rotRad = deg2rad(liftItem.rot || 0);
  // In this app's convention (see coord3d.js), an item with rot=0 has its
  // depth (fh) axis running north-south on screen — i.e. the item's own
  // "facing" direction at rot=0 is south, heading = +90°. So the two
  // candidate driving headings are rotRad+90° and rotRad+90°+180°,
  // representing threading the lift in one direction or the other.
  return [rotRad + Math.PI / 2, rotRad + Math.PI / 2 + Math.PI].map(heading => ({
    x: liftItem.cx, y: liftItem.cy, heading,
  }));
}

// Back-compat single-guess version (picks whichever candidate needs the
// smaller turn from fromHeading) — kept for anything that only wants one
// pose and doesn't need to try both.
export function liftTargetPose(liftItem, fromHeading) {
  const candidates = liftTargetPoses(liftItem).map(pose => {
    let diff = Math.abs(pose.heading - fromHeading) % (2 * Math.PI);
    if (diff > Math.PI) diff = 2 * Math.PI - diff;
    return { pose, diff };
  });
  candidates.sort((a, b) => a.diff - b.diff);
  return candidates[0].pose;
}

// ---------- Obstacle + bounds checking ----------

// Everything placed counts as an obstacle — rooms (real walls, like the
// stairs and washroom in the Dam's Ford blueprint), "obstruction" markers
// (structural columns), and equipment — but only through its SOLID parts
// (collision.itemSolids): a 2-post lift is its two posts, a lowered
// scissor/in-ground lift is drive-over, and the lift being driven ONTO
// keeps only its posts so its bay stays driveable.
function buildObstacles(placed, targetUid) {
  return placed.flatMap(item => itemSolids(item, { isTarget: item.uid === targetUid }));
}

// Checks a sampled path pose by pose: the vehicle's real body must stay
// at least 1 inch from every wall, door jamb and solid part (see
// collision.checkPose). Returns the first failure's reason.
function checkPath(points, ctx) {
  for (const p of points) {
    const hit = checkPose(p, ctx);
    if (hit) return { valid: false, reason: hit };
  }
  return { valid: true };
}

// ---------- Candidate staging-pose generation ----------

// Generates a small, fixed set of candidate "via poses" between two
// reference poses — used as the pivot point for a reverse maneuver. Not
// exhaustive by design (see file header); wide enough to cover the
// common real-world staging patterns (swing wide around whatever's in
// the way, then back in).
function stagingCandidates(fromPose, toPose, radius) {
  const dx = toPose.x - fromPose.x, dy = toPose.y - fromPose.y;
  const straightHeading = Math.atan2(dy, dx);
  const perp = straightHeading + Math.PI / 2;
  const midX = (fromPose.x + toPose.x) / 2, midY = (fromPose.y + toPose.y) / 2;
  const candidates = [];

  // Set A: offsets from the straight-line midpoint, biased toward modest
  // distances — with CCC (tight-turn) curves available, the vehicle
  // doesn't need to swing wide to reorient, so smaller offsets are often
  // enough and stay more likely to be inside the bay.
  const offsets = [radius * 0.4, radius * 0.8, radius * 1.3, radius * 2];
  const headingOptions = [straightHeading, fromPose.heading, toPose.heading];
  for (const mag of offsets) {
    for (const side of [1, -1]) {
      const bx = midX + Math.cos(perp) * mag * side;
      const by = midY + Math.sin(perp) * mag * side;
      for (const h of headingOptions) {
        candidates.push({ x: bx, y: by, heading: h });
      }
    }
  }

  // Set B: staging poses positioned just behind the TARGET pose, rotated
  // by a spread of angles — the classic "pull past at an angle, then
  // reverse straight in" pattern real drivers use for a tight dock.
  const distances = [radius * 0.5, radius * 0.9, radius * 1.4];
  const angleOffsetsDeg = [20, 40, 65, 90, -20, -40, -65, -90];
  for (const dist of distances) {
    for (const offDeg of angleOffsetsDeg) {
      const h = toPose.heading + (offDeg * Math.PI) / 180;
      candidates.push({
        x: toPose.x - dist * Math.cos(h),
        y: toPose.y - dist * Math.sin(h),
        heading: h,
      });
    }
  }

  // Set C: densely sampled points around the START's own turn circle (and
  // the TARGET's own turn circle), both turn directions. This is the set
  // that actually matters for a genuinely tight reorientation (small
  // travel distance, large heading change): a point on the vehicle's own
  // turn circle is reachable via a single short arc — no complex curve
  // needed to GET there — so the only hard part becomes the second leg
  // (that arc-exit-point to the goal), which the reverse Dubins/CCC solve
  // in dubins.js can now handle on its own. Sampling densely around both
  // circles, rather than guessing one "best" pivot analytically, is what
  // actually finds the tight three-point-turn a real driver would use.
  const turnCenter = (pose, turn) => {
    const sign = turn === 'L' ? 1 : -1;
    return { x: pose.x - sign * radius * Math.sin(pose.heading), y: pose.y + sign * radius * Math.cos(pose.heading) };
  };
  const SAMPLE_COUNT = 24;
  for (const anchorPose of [fromPose, toPose]) {
    for (const turn of ['L', 'R']) {
      const center = turnCenter(anchorPose, turn);
      const sign = turn === 'L' ? 1 : -1;
      for (let i = 0; i < SAMPLE_COUNT; i++) {
        const ang = (i / SAMPLE_COUNT) * 2 * Math.PI;
        const px = center.x + radius * Math.cos(ang);
        const py = center.y + radius * Math.sin(ang);
        const tangentHeading = ang + sign * (Math.PI / 2);
        candidates.push({ x: px, y: py, heading: tangentHeading });
        candidates.push({ x: px, y: py, heading: tangentHeading + Math.PI });
      }
    }
  }

  return candidates;
}

// ---------- Core point-to-point solver ----------

// How many alternate curves (per leg) to try before giving up on that leg
// — trying more than just the single shortest option matters because the
// shortest curve often swings wide into a wall or an obstacle while a
// slightly longer one tucked the other way fits fine.
const ALTS_PER_LEG = 3;

function opposite(gear) { return gear === 'reverse' ? 'forward' : 'reverse'; }
function plannerFor(gear) { return gear === 'reverse' ? planReverseAll : planForwardAll; }

// Solves "get from this pose to that pose, in this obstacle field, in at
// most 2 corrections" — the same 0/1/2-correction search described in the
// file header, extracted so it can be reused both for a direct door-to-
// lift attempt AND for each leg of the aisle-routing fallback below.
//
// `startGear` ('forward' by default) is which gear the FIRST leg drives
// in — every leg after that strictly alternates from there. Passing
// 'reverse' models a vehicle backing THROUGH the door first (paired with
// doorEntryPose(door, shop, true), whose heading points outward since the
// vehicle's nose stays pointed at the street while its body backs in) —
// a real alternative technique, not just nose-in-then-correct.
function solveBetweenPoses(startPose, goalPose, radius, ctx, startGear = 'forward') {
  const failureCounts = { obstacle: 0, space: 0, radius: 0 };
  const gearA = startGear;         // leg 1, 3, ...
  const gearB = opposite(startGear); // leg 2, 4, ...
  const planA = plannerFor(gearA);
  const planB = plannerFor(gearB);

  const direct = planA(startPose, goalPose, radius).slice(0, ALTS_PER_LEG);
  for (const leg of direct) {
    const points = sampleSegments(startPose, leg.segments, radius);
    const check = checkPath(points, ctx);
    if (check.valid) {
      return { valid: true, corrections: 0, segments: leg.segments, points };
    }
    failureCounts[check.reason]++;
  }
  if (direct.length === 0) failureCounts.radius++;

  let best1 = null;
  for (const apex of stagingCandidates(startPose, goalPose, radius)) {
    const legs1 = planA(startPose, apex, radius).slice(0, ALTS_PER_LEG);
    const legs2 = planB(apex, goalPose, radius).slice(0, ALTS_PER_LEG);
    if (legs1.length === 0 || legs2.length === 0) { failureCounts.radius++; continue; }
    for (const leg1 of legs1) {
      for (const leg2 of legs2) {
        const segments = [...leg1.segments, ...leg2.segments];
        const points = sampleSegments(startPose, segments, radius);
        const check = checkPath(points, ctx);
        if (!check.valid) { failureCounts[check.reason]++; continue; }
        const totalLen = leg1.length + leg2.length;
        if (!best1 || totalLen < best1.totalLen) best1 = { segments, points, totalLen };
      }
    }
  }
  if (best1) {
    return { valid: true, corrections: 1, segments: best1.segments, points: best1.points };
  }

  // Set C in stagingCandidates got a lot denser (needed for the 1-correction
  // search above to reliably find tight three-point-turns), so this stage
  // takes a much coarser slice of it — a full-density 2-leg product here
  // would be tens of thousands of combinations. This stage exists for the
  // rarer cases that need a genuine 2-correction maneuver at all.
  const apex1Candidates = stagingCandidates(startPose, goalPose, radius).filter((_, i) => i % 10 === 0);
  let best2 = null;
  for (const apex1 of apex1Candidates) {
    const legs1 = planA(startPose, apex1, radius).slice(0, 2);
    if (legs1.length === 0) { failureCounts.radius++; continue; }
    for (const leg1 of legs1) {
      const actualApex1 = endPose(startPose, leg1.segments, radius);
      for (const apex2 of stagingCandidates(actualApex1, goalPose, radius).filter((_, i) => i % 10 === 0)) {
        const legs2 = planB(actualApex1, apex2, radius).slice(0, 2);
        for (const leg2 of legs2) {
          const actualApex2 = endPose(actualApex1, leg2.segments, radius);
          const legs3 = planA(actualApex2, goalPose, radius).slice(0, 2);
          for (const leg3 of legs3) {
            const segments = [...leg1.segments, ...leg2.segments, ...leg3.segments];
            const points = sampleSegments(startPose, segments, radius);
            const check = checkPath(points, ctx);
            if (!check.valid) { failureCounts[check.reason]++; continue; }
            const totalLen = leg1.length + leg2.length + leg3.length;
            if (!best2 || totalLen < best2.totalLen) best2 = { segments, points, totalLen };
          }
        }
      }
    }
  }
  if (best2) {
    return { valid: true, corrections: 2, segments: best2.segments, points: best2.points };
  }

  // --- 3 corrections: A, B, A, B — a genuine multi-point turn. Sparse on
  // purpose (3 nested apex searches would explode combinatorially at full
  // density) — this stage exists so a vehicle that truly needs several
  // back-and-forth corrections to thread a tight, cluttered layout still
  // gets a real answer instead of an early "blocked," at the cost of
  // taking a bit longer to compute.
  const sparse = (fromPose, toPose) => stagingCandidates(fromPose, toPose, radius).filter((_, i) => i % 15 === 0).slice(0, 10);
  let best3 = null;
  for (const apex1 of sparse(startPose, goalPose)) {
    const leg1 = planA(startPose, apex1, radius)[0];
    if (!leg1) continue;
    const points1 = sampleSegments(startPose, leg1.segments, radius);
    if (!checkPath(points1, ctx).valid) continue;
    const actualApex1 = endPose(startPose, leg1.segments, radius);

    for (const apex2 of sparse(actualApex1, goalPose)) {
      const leg2 = planB(actualApex1, apex2, radius)[0];
      if (!leg2) continue;
      const points2 = sampleSegments(actualApex1, leg2.segments, radius);
      if (!checkPath(points2, ctx).valid) continue;
      const actualApex2 = endPose(actualApex1, leg2.segments, radius);

      for (const apex3 of sparse(actualApex2, goalPose)) {
        const leg3 = planA(actualApex2, apex3, radius)[0];
        if (!leg3) continue;
        const points3 = sampleSegments(actualApex2, leg3.segments, radius);
        if (!checkPath(points3, ctx).valid) continue;
        const actualApex3 = endPose(actualApex2, leg3.segments, radius);

        const leg4 = planB(actualApex3, goalPose, radius)[0];
        if (!leg4) continue;
        const segments = [...leg1.segments, ...leg2.segments, ...leg3.segments, ...leg4.segments];
        const points = sampleSegments(startPose, segments, radius);
        const check = checkPath(points, ctx);
        if (!check.valid) { failureCounts[check.reason]++; continue; }
        const totalLen = leg1.length + leg2.length + leg3.length + leg4.length;
        if (!best3 || totalLen < best3.totalLen) best3 = { segments, points, totalLen };
      }
    }
  }
  if (best3) {
    return { valid: true, corrections: 3, segments: best3.segments, points: best3.points };
  }

  // Everything above (0 through 3 corrections) is hand-tuned and
  // exhaustive-within-its-density — proven reliable, left untouched. If
  // ALL of it fails, this keeps going rather than reporting "blocked" at
  // a fixed cap: a real driver isn't limited to 3 gear changes, so
  // neither should the search be. Not literally infinite (a real-time UI
  // needs a ceiling somewhere), but it goes well past what any realistic
  // parking maneuver should ever need — hitting ITS ceiling means the
  // approach is genuinely not geometrically possible for this vehicle,
  // not that the search gave up early.
  const deep = solveDeepCorrections(startPose, goalPose, radius, ctx, failureCounts, startGear);
  if (deep) return deep;

  const topReason = Object.entries(failureCounts).sort((a, b) => b[1] - a[1])[0][0];
  return { valid: false, reason: topReason, failureCounts };
}

// How many correction levels the deep fallback above will try beyond the
// hand-tuned 0-3 stages (i.e. it can report up to this many TOTAL
// corrections) before finally giving up, and how many candidate "threads"
// it keeps alive between stages (a beam search — at each stage, keep only
// the shortest-so-far BEAM_WIDTH partial paths and extend those, rather
// than letting the candidate count explode combinatorially with depth).
const DEEP_MAX_CORRECTIONS = 8;
const DEEP_BEAM_WIDTH = 5;

function solveDeepCorrections(startPose, goalPose, radius, ctx, failureCounts, startGear = 'forward') {
  let frontier = [{ pose: startPose, segments: [], length: 0 }];

  for (let s = 0; s <= DEEP_MAX_CORRECTIONS; s++) {
    // Gear strictly alternates starting from startGear — leg 1 uses
    // startGear, leg 2 the opposite, leg 3 back to startGear, etc. — so
    // which gear is "next" is fully determined by s, no need to track it
    // per-state. A path that completes at stage s has taken s+1 legs with
    // s gear changes between them, i.e. corrections === s.
    const gear = s % 2 === 0 ? startGear : opposite(startGear);
    const planAll = plannerFor(gear);

    let best = null;
    for (const state of frontier) {
      const legs = planAll(state.pose, goalPose, radius).slice(0, 2);
      for (const leg of legs) {
        const segments = [...state.segments, ...leg.segments];
        const points = sampleSegments(startPose, segments, radius);
        const check = checkPath(points, ctx);
        if (!check.valid) { failureCounts[check.reason]++; continue; }
        const totalLen = state.length + leg.length;
        if (!best || totalLen < best.totalLen) best = { segments, points, totalLen };
      }
    }
    if (best) return { valid: true, corrections: s, segments: best.segments, points: best.points };
    if (s === DEEP_MAX_CORRECTIONS) break;

    // Expand each surviving thread by one more leg for the next stage —
    // sparse on purpose, this is already the last-resort fallback so it
    // can afford to be slower than the earlier stages, but still needs a
    // hard bound per stage or it would never return.
    const nextFrontier = [];
    for (const state of frontier) {
      const candidates = stagingCandidates(state.pose, goalPose, radius).filter((_, i) => i % 20 === 0).slice(0, 8);
      for (const apex of candidates) {
        const leg = planAll(state.pose, apex, radius)[0];
        if (!leg) continue;
        const actualApex = endPose(state.pose, leg.segments, radius);
        nextFrontier.push({ pose: actualApex, segments: [...state.segments, ...leg.segments], length: state.length + leg.length });
      }
    }
    nextFrontier.sort((a, b) => a.length - b.length);
    frontier = nextFrontier.slice(0, DEEP_BEAM_WIDTH);
    if (frontier.length === 0) break;
  }
  return null;
}

// ---------- Aisle routing (multi-lift rows) ----------
//
// A door rarely lines up with a specific lift's own drive-through axis —
// in a real shop, the door opens onto a shared aisle, and each lift is
// approached by turning INTO its bay from that aisle, not by driving
// straight at it from the door. Trying to solve door-to-lift as one
// blind maneuver falls apart the moment there's a NEIGHBORING lift in
// the row blocking the direct swing (exactly what happens with two
// 2-post lifts side by side, sharing one row).
//
// This models that by proposing a handful of "aisle waypoints" — points
// out along the lift's OWN access axis, on whichever end has room — and
// solving it as two separate, much easier problems: door -> waypoint
// (open space, few obstacles in the way), then waypoint -> lift (already
// lined up, usually a straight shot). Tried alongside the direct
// door-to-lift attempt; whichever combination needs fewer corrections
// wins.
function solveViaAisle(startPose, liftItem, radius, ctx, startGear = 'forward') {
  const rotRad = deg2rad(liftItem.rot || 0);
  const axisHeadings = [rotRad + Math.PI / 2, rotRad + Math.PI / 2 + Math.PI];
  const distances = [radius * 0.8, radius * 1.3, radius * 2, radius * 3];

  let best = null;
  for (const heading of axisHeadings) {
    const goalPose = { x: liftItem.cx, y: liftItem.cy, heading };
    for (const dist of distances) {
      const waypoint = {
        x: liftItem.cx - dist * Math.cos(heading),
        y: liftItem.cy - dist * Math.sin(heading),
        heading,
      };
      // Check the CHEAP leg first — waypoint is already lined up with the
      // lift, so this is almost always an instant 0-correction straight
      // line, or a quick failure if the waypoint itself is too close to
      // an obstacle. Only run the expensive door->waypoint search (which
      // does a full 0/1/2-correction sweep) if this one's actually usable.
      // (legB always starts fresh at 'forward' — treating the two legs'
      // gear sequences as independent is a known simplification; the rare
      // case where legA happens to end mid-reverse right at the waypoint
      // isn't specially accounted for here.)
      const legB = solveBetweenPoses(waypoint, goalPose, radius, ctx);
      if (!legB.valid) continue;
      const legA = solveBetweenPoses(startPose, waypoint, radius, ctx, startGear);
      if (!legA.valid) continue;
      const totalCorrections = legA.corrections + legB.corrections;
      const segments = [...legA.segments, ...legB.segments];
      const points = [...legA.points, ...legB.points.slice(1)]; // avoid duplicating the waypoint sample
      if (!best || totalCorrections < best.corrections) {
        best = { valid: true, corrections: totalCorrections, segments, points, goalPose };
      }
      // Good enough — stop searching once we have a 1-correction-or-better
      // combo; further distances/headings are unlikely to beat it and
      // this keeps the search fast enough for an on-click UI action.
      if (totalCorrections <= 1) return best;
    }
  }
  return best;
}

// ---------- Main solver ----------

// Returns:
//   { valid: true, corrections, segments, points, startPose, goalPose }
//   { valid: false, reason: 'obstacle'|'space'|'radius', corrections: null }
export function planApproach({ shop, door, doors, liftItem, vehicleKey, placed }) {
  const vehicle = getVehicle(vehicleKey);
  const radius = vehicle.turningRadius;

  // Two entry strategies: drive in nose-first (Forward), or back through
  // the door first (Reverse) — a real technique that can line the vehicle
  // up better for lifts on the "wrong side" relative to a forward entry.
  // Both are tried; whichever needs fewer corrections wins. See
  // doorEntryPose's `reverse` param and solveBetweenPoses' `startGear`.
  const startPoseFwd = doorEntryPose(door, shop, false);
  const startPoseRev = doorEntryPose(door, shop, true);
  if (!startPoseFwd) return { valid: false, reason: 'space' };

  const bbox = computeBbox(shop.vertices);
  const obstacles = buildObstacles(placed, liftItem.uid);
  // Walls get every overhead opening cut out; the active door must be one
  // of them even if the caller didn't pass the full list
  const walls = wallSegments(shop, doors && doors.length ? doors : [door]);
  const ctx = { bbox, obstacles, walls, shop, vehicle, doorPoint: { x: startPoseFwd.x, y: startPoseFwd.y } };

  // Try the direct door-to-lift maneuver — against BOTH of the lift's
  // symmetric end-on headings (a "closer heading" guess isn't reliable,
  // since the actually-reachable end depends on what's around the lift)
  // AND both entry strategies — plus the aisle-routed alternative for
  // each entry strategy — then use whichever combination needs fewer
  // corrections overall.
  //
  // IMPORTANT for performance: each of those is itself an expensive call
  // (solveBetweenPoses can fall all the way through to the ~1-2s deep
  // fallback if it fails), and there are up to 6 of them (2 entry
  // strategies x 2 goal headings, +2 aisle variants). Running all 6 on
  // every single vehicle-dropdown change was the "heavy CPU load"
  // problem — most real approaches resolve easily (0-2 corrections) on
  // the FIRST or second attempt, so this now stops as soon as it finds a
  // result that's already good, only escalating through the remaining,
  // pricier options when it actually needs to.
  const GOOD_ENOUGH_CORRECTIONS = 2;
  // Only lift ends where the parked vehicle itself fits (1" clear of posts,
  // walls and neighbours). If neither end fits, no path can work — say so
  // right away instead of running the whole (slow) search to find out.
  const candidatePoses = liftTargetPoses(liftItem);
  const allTargetPoses = candidatePoses.filter(p => !checkPose(p, ctx));
  if (allTargetPoses.length === 0) {
    return {
      valid: false,
      corrections: null,
      reason: 'obstacle',
      message: 'this vehicle doesn\'t fit on the lift where it\'s placed — parked, it would be within 1" of a post, wall or other item',
      startPose: startPoseFwd,
      goalPose: candidatePoses[0],
    };
  }
  const attempts = [];
  const bestSoFar = () => {
    const valid = attempts.filter(a => a.valid);
    if (valid.length === 0) return null;
    valid.sort((a, b) => a.corrections - b.corrections);
    return valid[0];
  };

  // Cheapest, most-common case first: forward (nose-in) entry, both lift ends.
  for (const goalPose of allTargetPoses) {
    attempts.push({ ...solveBetweenPoses(startPoseFwd, goalPose, radius, ctx, 'forward'), goalPose, startPose: startPoseFwd });
    if (bestSoFar()?.corrections <= GOOD_ENOUGH_CORRECTIONS) break;
  }

  // Next cheapest: back-in entry, both lift ends — only if forward entry
  // didn't already find something good.
  if (!bestSoFar() || bestSoFar().corrections > GOOD_ENOUGH_CORRECTIONS) {
    for (const goalPose of allTargetPoses) {
      attempts.push({ ...solveBetweenPoses(startPoseRev, goalPose, radius, ctx, 'reverse'), goalPose, startPose: startPoseRev });
      if (bestSoFar()?.corrections <= GOOD_ENOUGH_CORRECTIONS) break;
    }
  }

  // Aisle routing (both entry strategies) — the most expensive fallback,
  // only run when direct attempts (both entries) didn't find anything good.
  if (!bestSoFar() || bestSoFar().corrections > GOOD_ENOUGH_CORRECTIONS) {
    const viaAisleFwd = solveViaAisle(startPoseFwd, liftItem, radius, ctx, 'forward');
    if (viaAisleFwd && viaAisleFwd.valid) attempts.push({ ...viaAisleFwd, startPose: startPoseFwd });
  }
  if (!bestSoFar() || bestSoFar().corrections > GOOD_ENOUGH_CORRECTIONS) {
    const viaAisleRev = solveViaAisle(startPoseRev, liftItem, radius, ctx, 'reverse');
    if (viaAisleRev && viaAisleRev.valid) attempts.push({ ...viaAisleRev, startPose: startPoseRev });
  }

  const winner = bestSoFar();
  if (winner) {
    return { valid: true, corrections: winner.corrections, segments: winner.segments, points: winner.points, startPose: winner.startPose, goalPose: winner.goalPose };
  }

  const directAttempts = attempts;

  // Neither approach worked (either end-on heading, direct or via-aisle) —
  // report why, preferring whichever direct attempt's reason since that's
  // most directly tied to the door/lift pair the user actually picked.
  const reasonMessages = {
    obstacle: 'the swept path keeps clipping another placed item — try relocating it or the lift',
    space: 'the vehicle would need to leave the bay to complete the turn — needs a wider bay or a different door',
    radius: 'this vehicle\'s turning radius is too large to reach the lift at any tried angle',
  };
  const reason = directAttempts[0]?.reason || 'space';
  return {
    valid: false,
    corrections: null,
    reason,
    message: reasonMessages[reason] || 'no valid approach found',
    startPose: startPoseFwd,
    goalPose: allTargetPoses[0],
  };
}

// Convenience: given a lift item, find the nearest overhead door on the
// shop, so the caller doesn't have to pick one manually every time.
export function nearestOverheadDoor(liftItem, doors, shop) {
  let best = null, bestDist = Infinity;
  for (const door of doors) {
    if (door.type !== 'overhead') continue;
    const ends = getDoorEndpoints(door, shop);
    if (!ends) continue;
    const d = Math.hypot(ends.midpoint.x - liftItem.cx, ends.midpoint.y - liftItem.cy);
    if (d < bestDist) { bestDist = d; best = door; }
  }
  return best;
}

// Catalog ids this simulation knows how to build a target pose for — used
// by CadCanvas/Canvas3D to decide whether the currently-selected placed
// item is a lift the "simulate approach" toggle should show up for.
export const SIMULATABLE_LIFT_IDS = Object.keys(APPROACH_TOLERANCE);

export { APPROACH_TOLERANCE };

// ---------- Manual path editing ----------
//
// The auto-solver above is a bounded heuristic search — it can (and, on a
// cluttered real drawing, does) report "blocked" for an approach a human
// knows is actually fine, because the real driver would thread a gap or
// take a line the search's candidate set didn't happen to sample. Rather
// than trying to make the search exhaustive (never fully possible), this
// gives the user a manual override: add as many via-poses (position +
// heading) as needed, each with its own forward/reverse choice for the
// leg arriving at it, and the app solves each leg directly with the
// single best curve — no search, since the user is placing the points on
// purpose (e.g. to judge how close they can cut it past a neighboring
// lift). Still runs the same collision check as auto mode and reports if
// it's clipping something, but doesn't block playback on that — the
// person placing the points can see the warning and judge for themselves,
// the same way they'd eyeball it on paper.
//
// A waypoint is { x, y, heading, gear }, where `gear` ('forward' or
// 'reverse') describes the leg ARRIVING at that waypoint, i.e. the drive
// from whatever pose comes before it (the door, or the previous waypoint)
// to this one. The final leg into the lift has its own gear passed in
// separately (`goalGear`) since the goal pose itself isn't a waypoint the
// user drags.

// Everything a manual (or auto) solve needs about the surroundings, minus
// the door-specific start pose — split out so the overlay can build this
// once and reuse it for both the auto result and every manual edit,
// rather than recomputing obstacles/bbox on every drag.
export function buildApproachContext({ shop, doors = [], liftItem, vehicleKey, placed }) {
  const vehicle = getVehicle(vehicleKey);
  const radius = vehicle.turningRadius;
  const bbox = computeBbox(shop.vertices);
  const obstacles = buildObstacles(placed, liftItem.uid);
  const walls = wallSegments(shop, doors);
  return { vehicle, radius, bbox, obstacles, walls, shop };
}

// A sensible default waypoint to start from when the user adds their
// first point: the straight-line midpoint between the two poses it'll
// sit between, facing the far one — not meant to already be a good path,
// just a visible starting handle to drag into place.
export function defaultManualWaypoint(fromPose, toPose, gear) {
  const dx = toPose.x - fromPose.x, dy = toPose.y - fromPose.y;
  return {
    x: (fromPose.x + toPose.x) / 2,
    y: (fromPose.y + toPose.y) / 2,
    heading: Math.atan2(dy, dx),
    gear,
  };
}

// Picks the best USABLE curve for one manual leg rather than blindly
// taking the mathematically shortest one. Why this matters: a Dubins/CCC
// solver returns the globally shortest curve for the pose pair you gave
// it — but "shortest" only means shortest, not "sane." When the two
// poses are close together with a heading that doesn't line up with a
// tight three-point-turn from there, the shortest valid curve can be a
// huge loop that swings out of the building before coming back — the
// turning radius is a real physical constraint, and if the only curves
// that satisfy it happen to be big loops, that's genuinely what the
// vehicle would have to do FROM THAT EXACT PLACEMENT. So instead of
// showing just the single (possibly building-exiting) shortest option,
// this checks the next few alternates and prefers the shortest one that
// actually stays within the shop's bounds — usually a much saner curve
// exists a few feet longer than the absolute shortest.
function bestUsableLeg(fromPose, toPose, radius, gear, bbox) {
  const all = gear === 'reverse' ? planReverseAll(fromPose, toPose, radius) : planForwardAll(fromPose, toPose, radius);
  if (all.length === 0) return null;
  const slack = radius * 0.25; // a little breathing room past the walls, not a hard collision check
  for (const leg of all.slice(0, 6)) {
    const points = sampleSegments(fromPose, leg.segments, radius);
    const inBounds = points.every(p =>
      p.x > bbox.minX - slack && p.x < bbox.maxX + slack &&
      p.y > bbox.minY - slack && p.y < bbox.maxY + slack);
    if (inBounds) return leg;
  }
  return all[0]; // nothing stayed in-bounds — fall back to shortest rather than nothing
}

// Solves a full manual chain: door -> waypoint[0] -> waypoint[1] -> ... ->
// lift, each leg driven with the best USABLE curve (see bestUsableLeg
// above) in whichever gear that leg is set to. `corrections` counts
// actual gear CHANGES along the chain (two forward legs in a row is still
// 0 extra corrections between them), matching how the auto-solver counts
// them.
export function solveManualPath(startPose, waypoints, goalPose, goalGear, radius, ctx) {
  const poses = [startPose, ...waypoints, goalPose];
  const gears = [...waypoints.map(w => w.gear), goalGear];
  const allSegments = [];
  let corrections = 0;
  let prevGear = null;

  for (let i = 0; i < poses.length - 1; i++) {
    const gear = gears[i];
    const leg = bestUsableLeg(poses[i], poses[i + 1], radius, gear, ctx.bbox);
    if (!leg) {
      return {
        valid: false,
        reason: 'radius',
        message: `this vehicle can't curve tightly enough between point ${i} and point ${i + 1} — try moving one of them, or loosening the heading`,
        corrections: null,
      };
    }
    allSegments.push(...leg.segments);
    if (prevGear !== null && prevGear !== gear) corrections++;
    prevGear = gear;
  }

  const points = sampleSegments(startPose, allSegments, radius);
  const check = checkPath(points, ctx);
  return {
    valid: check.valid,
    reason: check.reason,
    message: check.valid ? null : 'this path clips a placed item or the bay wall — drag a point to clear it',
    corrections,
    segments: allSegments,
    points,
  };
}
